import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles - using design system variables with mobile responsiveness
  'inline-flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-lg text-fluid-sm sm:text-fluid-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:size-3.5 sm:[&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary action button - vibrant gradient with clear states
        primary:
          'glass bg-gradient-primary btn-primary-glow text-background shadow-gentle hover:shadow-card hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] active:opacity-100 disabled:opacity-40 disabled:shadow-none',

        // Secondary action - teal/blue gradient with clear states
        secondary:
          'glass bg-gradient-secondary btn-secondary-glow text-background shadow-gentle hover:shadow-card hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] active:opacity-100 disabled:opacity-40 disabled:shadow-none',

        // Accent/Highlight action - purple/pink gradient with clear states
        accent:
          'glass bg-gradient-accent btn-accent-glow text-background shadow-gentle hover:shadow-card hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] active:opacity-100 disabled:opacity-40 disabled:shadow-none',

        // Destructive/Delete action - warm gradient with clear states
        destructive:
          'glass bg-gradient-warm btn-warm-glow text-background shadow-gentle hover:shadow-card hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] active:opacity-100 disabled:opacity-40 disabled:shadow-none',

        // Outline button - subtle with visible state changes
        outline:
          'glass-subtle hover:glass text-foreground hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40 btn-primary-glow hover:text-primary',

        // Ghost button - glass background with hover effects
        ghost:
          'hover:glass text-foreground hover:glass hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40',

        // Ghost invisible button - glass background with hover effects
        ghostInvisible:
          'text-foreground btn-primary-glow hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40',

        // Link button - text only with visible state changes
        link: 'text-primary underline-offset-4 hover:underline hover:opacity-80 hover:scale-[1.01] active:opacity-100 active:scale-[0.99] disabled:opacity-40 disabled:no-underline',
      },
      size: {
        sm: 'h-8 sm:h-9 px-4 sm:px-5 text-fluid-xs',
        md: 'h-9 sm:h-10 px-5 sm:px-6 py-2',
        default: 'h-10 sm:h-11 px-6 sm:px-8 py-2 sm:py-2.5',
        lg: 'h-11 sm:h-12 md:h-13 px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 text-fluid-sm',
        icon: 'h-9 w-9 sm:h-10 sm:w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Overrides aria-label for the hover tooltip. Pass `false` to suppress. */
  tooltip?: string | false
  /** Tooltip style. Defaults to arrow. Pass `default` for no pointer. */
  tooltipVariant?: 'default' | 'arrow'
  /**
   * Preferred tooltip side. Defaults to `bottom` so header/nav buttons
   * aren't covered by the sticky app chrome. Radix flips on collision;
   * the arrow always points toward the button.
   */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tooltip,
      tooltipVariant = 'default',
      tooltipSide = 'top',
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const label =
      tooltip === false
        ? undefined
        : (tooltip ?? (typeof ariaLabel === 'string' ? ariaLabel : undefined))

    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      />
    )

    if (!label) {
      return button
    }

    // Disabled buttons use pointer-events-none, so wrap the trigger so hover still works.
    const trigger = props.disabled ? <span className="inline-flex">{button}</span> : button

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent
            variant={tooltipVariant}
            side={tooltipSide}
            // Fixed app header (~64px) + sticky page header — treat top as occupied
            // so near-top buttons flip below instead of rendering under the nav.
            collisionPadding={{ top: 72 }}
          >
            <p className="text-fluid-xs">{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
